import { inject, Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { createActionStream } from '@core/ngrx-legacy';
import {
  ManageSeatsPayload,
  TellentMembersActions,
  TellentMembersSelectors,
} from '@core/tellent-data';
import {
  InviteMemberPayload,
  MemberDataUpdate,
  type TeamMembersPaginationParams,
  TellentOrgAbilities,
} from '@core/tellent-types';

@Injectable({
  providedIn: 'root',
})
export class TellentMembersDataFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public readonly inviteTellentMemberSuccess$ = createActionStream(
    TellentMembersActions.inviteTellentMemberSuccess,
  );
  public readonly inviteTellentMemberFail$ = createActionStream(
    TellentMembersActions.inviteTellentMemberFail,
  );
  public readonly inviteAppMemberSuccess$ = createActionStream(
    TellentMembersActions.inviteAppMemberSuccess,
  );
  public readonly inviteAppMemberFail$ = createActionStream(
    TellentMembersActions.inviteAppMemberFail,
  );
  public readonly manageSeatsSuccess$ = createActionStream(
    TellentMembersActions.manageSeatsSuccess,
  );
  public readonly manageSeatsFailed$ = createActionStream(TellentMembersActions.manageSeatsFailed);
  public readonly deleteMemberSuccess$ = createActionStream(
    TellentMembersActions.deleteMemberSuccess,
  );
  public readonly updateMemberSuccess$ = createActionStream(
    TellentMembersActions.updateMemberSuccess,
  );
  public readonly updateMemberFail$ = createActionStream(TellentMembersActions.updateMemberFail);
  public readonly deactivateMemberSuccess$ = createActionStream(
    TellentMembersActions.deactivateMemberSuccess,
  );
  public readonly reactivateMemberSuccess$ = createActionStream(
    TellentMembersActions.reactivateMemberSuccess,
  );
  public readonly sendInvitationSuccess$ = createActionStream(
    TellentMembersActions.sendInvitationSuccess,
  );
  public readonly updateAbilitiesSuccess$ = createActionStream(
    TellentMembersActions.updateAbilitiesSuccess,
  );
  public readonly updateAbilitiesFail$ = createActionStream(
    TellentMembersActions.updateAbilitiesFail,
  );

  public teamMemberById(guid: string) {
    return this.store.selectSignal(TellentMembersSelectors.teamMemberById(guid));
  }

  public teamMembersPaginated(scope: string) {
    return this.store.selectSignal(TellentMembersSelectors.entitiesWithPagination({ scope }));
  }
  public teamMembersMeta(idOrScope: { scope: string } | string) {
    return this.store.selectSignal(TellentMembersSelectors.meta(idOrScope));
  }

  public fetchOne(guid: string) {
    this.store.dispatch(TellentMembersActions.fetchOne({ payload: { id: guid } }));
  }

  public inviteTellentMember(payload: InviteMemberPayload) {
    this.store.dispatch(TellentMembersActions.inviteTellentMember({ payload }));
  }

  public inviteAppMember(payload: InviteMemberPayload) {
    this.store.dispatch(TellentMembersActions.inviteAppMember({ payload }));
  }

  public reactivateMember(guid: string) {
    this.store.dispatch(TellentMembersActions.reactivateMember({ payload: { guid } }));
  }

  public sendInvitation(guid: string) {
    this.store.dispatch(TellentMembersActions.sendInvitation({ payload: { guid } }));
  }

  public deleteMember(guid: string) {
    this.store.dispatch(TellentMembersActions.deleteMember({ payload: { guid } }));
  }

  public deactivateMember(guid: string) {
    this.store.dispatch(TellentMembersActions.deactivateMember({ payload: { guid } }));
  }

  public fetchPaginatedCollection(params: TeamMembersPaginationParams, add?: boolean) {
    this.store.dispatch(
      TellentMembersActions.fetchPaginatedCollection({
        payload: { params, add },
      }),
    );
  }

  public updateMember(data: MemberDataUpdate) {
    this.store.dispatch(
      TellentMembersActions.updateMember({
        payload: data,
      }),
    );
  }

  public sendPasswordReset(guid: string) {
    this.store.dispatch(
      TellentMembersActions.sendPasswordReset({
        payload: {
          guid,
        },
      }),
    );
  }

  public manageSeats(payload: ManageSeatsPayload) {
    this.store.dispatch(
      TellentMembersActions.manageSeats({
        payload,
      }),
    );
  }

  public updateAbilities(payload: { guid: string; abilities: TellentOrgAbilities[] }) {
    this.store.dispatch(
      TellentMembersActions.updateAbilities({
        payload,
      }),
    );
  }
}
